// src/pages/AboutUs.js

import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';

import heroBg from '../assets/about_hero.png';
import whoWeAreImg from '../assets/who_we_are.png';
import ourStoryImg from '../assets/our_story.png';

function AboutUs() {
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const coreValues = [
    {
      title: "Integrated Expertise",
      desc: "A seamless blend of data science, financial management, and corporate strategy.",
      icon: (
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#E1261C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
        </svg>
      )
    },
    {
      title: "Uncompromising Security",
      desc: "Adherence to the highest global standards for data privacy, confidentiality, and information security management.",
      icon: (
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#E1261C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
        </svg>
      )
    },
    {
      title: "Client-Centric Agility",
      desc: "Customized, cost-effective solutions designed around your specific operational environment.",
      icon: (
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#E1261C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/>
        </svg>
      )
    }
  ];

  return (
    <div style={{ fontFamily: 'Arial, sans-serif', color: '#333' }}>
      
      {/* 1. Hero Header */}
      <section style={styles.heroSection}>
        <div style={styles.heroOverlay}></div>
        <div style={styles.heroContent}>
          <h1 style={styles.heroTitle}>Empowering Your Growth Through Intelligent Solutions.</h1>
          <p style={styles.heroSubtitle}>
            At Naprev, we merge strategic business insight with advanced technological innovation to help you make smarter, faster, and more profitable decisions.
          </p>
          <Link to="/contact" style={styles.heroButton}>Connect With Us</Link>
        </div>
      </section>

      {/* 2. Who We Are */}
      <section style={styles.zigZagSection}>
        <div style={styles.zigZagContainer}>
          <div style={styles.textContent}>
            <h2 style={styles.sectionTitle}>Who We Are</h2>
            <div style={styles.titleUnderline}></div>
            <p style={styles.paragraph}>
              We are a dynamically driven, independent global organization dedicated to simplifying complex business challenges. Operating at the intersection of business strategy and advanced analytics, Naprev provides integrated, digitally-driven solutions tailored to your unique lifecycle stage.
            </p>
            <p style={styles.paragraph}>
              Whether you are a rapidly scaling startup, a mid-market enterprise, or a multinational corporation, we act as your strategic partners—translating ambitious plans into tangible, efficient outcomes within the financial advisory and accounts outsourcing markets.
            </p>
          </div>
          <div style={styles.imageContent}>
            <div style={{...styles.placeholderImage, backgroundImage: `url(${whoWeAreImg})`}}>
              {!whoWeAreImg && "[ Who We Are Image Placeholder ]"}
            </div>
          </div>
        </div>
      </section>

      {/* 3. Our Story */}
      <section style={{...styles.zigZagSection, backgroundColor: '#F9FAFB'}}>
        <div style={{...styles.zigZagContainer, flexDirection: 'row-reverse'}}>
          <div style={styles.textContent}>
            <h2 style={styles.sectionTitle}>Our Story</h2>
            <div style={styles.titleUnderline}></div>
            <p style={styles.paragraph}>
              Naprev was founded by a team of industry veterans who intimately understand the challenges of scaling operations, optimizing workflows, and managing investor expectations. 
            </p>
            <p style={styles.paragraph}>
              Frustrated by fragmented data and siloed consulting approaches, we built Naprev to bring everything under one roof: deep financial knowledge, operational expertise, and cutting-edge software engineering.
            </p>
          </div>
          <div style={styles.imageContent}>
            <div style={{...styles.placeholderImage, backgroundImage: `url(${ourStoryImg})`}}>
              {!ourStoryImg && "[ Timeline / Founders Image Placeholder ]"}
            </div>
          </div>
        </div>
      </section>

      {/* 4. Mission & Vision */}
      <section style={styles.missionVisionSection}>
        <div style={styles.cardGrid}>
          {/* Mission Card */}
          <div style={styles.mvCard}>
            <div style={styles.mvIconWrapper}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#002B49" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/><path d="M16.24 7.76l-2.12 6.36-6.36 2.12 2.12-6.36 6.36-2.12z"/>
              </svg>
            </div>
            <h3 style={styles.mvTitle}>Our Mission</h3>
            <p style={styles.mvDesc}>
              To provide proactive, tech-enabled business and financial advice that simplifies operations, ensures compliance, and drives sustainable commercial growth for our clients.
            </p>
          </div>

          {/* Vision Card */}
          <div style={styles.mvCard}>
            <div style={styles.mvIconWrapper}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#002B49" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
              </svg>
            </div>
            <h3 style={styles.mvTitle}>Our Vision</h3>
            <p style={styles.mvDesc}>
              To be the most trusted global partner in business intelligence and consulting, delivering accuracy, reliability, and precision insights that empower every organizational decision.
            </p>
          </div>
        </div>
      </section>

      {/* 5. Quality & Core Values */}
      <section style={styles.valuesSection}>
        <div style={styles.valuesHeader}>
          <h2 style={styles.sectionTitle}>Quality & Core Values</h2>
          <div style={{...styles.titleUnderline, margin: '0 auto 40px auto'}}></div>
        </div>
        <div style={styles.valuesGrid}>
          {coreValues.map((value, index) => (
            <div key={index} style={styles.valueItem}>
              <div style={styles.valueIcon}>{value.icon}</div>
              <h4 style={styles.valueTitle}>{value.title}</h4>
              <p style={styles.valueDesc}>{value.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* 6. Footer Call-to-Action */}
      <section style={styles.footerCTA}>
        <h2 style={styles.ctaTitle}>Your Next Phase of Success Starts Here.</h2>
        <p style={styles.ctaSubtitle}>Discover how Naprev can transform your business workflows and strategic decision-making.</p>
        <Link to="/contact" style={styles.ctaButton}>Request a Free Consultation</Link>
      </section>

    </div>
  );
}

const styles = {
  heroSection: {
    position: 'relative',
    width: '100%',
    height: '60vh',
    minHeight: '400px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#001524',
    backgroundImage: `url(${heroBg})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
  },
  heroOverlay: {
    position: 'absolute',
    top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0, 43, 73, 0.75)',
    zIndex: 1
  },
  heroContent: {
    position: 'relative',
    zIndex: 2,
    maxWidth: '900px',
    textAlign: 'center',
    padding: '0 20px'
  },
  heroTitle: {
    fontSize: '48px',
    color: '#FFFFFF',
    fontWeight: 'bold',
    marginBottom: '20px',
    lineHeight: '1.2'
  },
  heroSubtitle: {
    fontSize: '20px',
    color: '#E5E7EB',
    marginBottom: '40px',
    lineHeight: '1.6'
  },
  heroButton: {
    display: 'inline-block',
    backgroundColor: '#E1261C',
    color: '#FFFFFF',
    padding: '15px 35px',
    fontSize: '18px',
    fontWeight: 'bold',
    textDecoration: 'none',
    borderRadius: '6px',
    transition: 'background-color 0.3s'
  },

  zigZagSection: {
    padding: '80px 5%',
    backgroundColor: '#FFFFFF'
  },
  zigZagContainer: {
    maxWidth: '1200px',
    margin: '0 auto',
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '50px'
  },
  textContent: {
    flex: 1,
    minWidth: '320px'
  },
  imageContent: {
    flex: 1,
    minWidth: '320px',
    display: 'flex',
    justifyContent: 'center'
  },
  placeholderImage: {
    width: '100%',
    height: '400px',
    backgroundColor: '#E5E7EB',
    borderRadius: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#9CA3AF',
    fontSize: '18px',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    boxShadow: '0 10px 25px -5px rgba(0,0,0,0.1)'
  },
  sectionTitle: {
    fontSize: '36px',
    color: '#002B49',
    marginBottom: '15px',
    fontWeight: 'bold'
  },
  titleUnderline: {
    height: '4px',
    width: '60px',
    backgroundColor: '#E1261C',
    marginBottom: '25px',
    borderRadius: '2px'
  },
  paragraph: {
    fontSize: '17px',
    color: '#4B5563',
    lineHeight: '1.7',
    marginBottom: '20px',
    textAlign: 'justify'
  },

  missionVisionSection: {
    padding: '80px 5%',
    backgroundColor: '#F3F4F6'
  },
  cardGrid: {
    maxWidth: '1000px',
    margin: '0 auto',
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
    gap: '40px'
  },
  mvCard: {
    backgroundColor: '#FFFFFF',
    padding: '50px 40px',
    borderRadius: '16px',
    boxShadow: '0 20px 25px -5px rgba(0,0,0,0.05)',
    textAlign: 'center',
    borderTop: '5px solid #002B49'
  },
  mvIconWrapper: {
    marginBottom: '25px',
    display: 'inline-flex'
  },
  mvTitle: {
    fontSize: '28px',
    color: '#002B49',
    marginBottom: '20px',
    fontWeight: 'bold'
  },
  mvDesc: {
    fontSize: '17px',
    color: '#4B5563',
    lineHeight: '1.6'
  },

  valuesSection: {
    padding: '80px 5%',
    backgroundColor: '#FFFFFF'
  },
  valuesHeader: {
    textAlign: 'center',
    maxWidth: '800px',
    margin: '0 auto'
  },
  valuesGrid: {
    maxWidth: '1200px',
    margin: '40px auto 0',
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: '40px'
  },
  valueItem: {
    textAlign: 'center',
    padding: '20px'
  },
  valueIcon: {
    width: '80px',
    height: '80px',
    backgroundColor: '#Fef2f2',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 20px auto'
  },
  valueTitle: {
    fontSize: '22px',
    color: '#002B49',
    marginBottom: '15px',
    fontWeight: 'bold'
  },
  valueDesc: {
    fontSize: '16px',
    color: '#4B5563',
    lineHeight: '1.6'
  },

  footerCTA: {
    backgroundColor: '#002B49',
    padding: '80px 5%',
    textAlign: 'center',
    color: '#FFFFFF'
  },
  ctaTitle: {
    fontSize: '36px',
    fontWeight: 'bold',
    marginBottom: '20px'
  },
  ctaSubtitle: {
    fontSize: '19px',
    color: '#9CA3AF',
    marginBottom: '40px',
    maxWidth: '700px',
    margin: '0 auto 40px auto'
  },
  ctaButton: {
    display: 'inline-block',
    backgroundColor: '#E1261C',
    color: '#FFFFFF',
    padding: '16px 40px',
    fontSize: '18px',
    fontWeight: 'bold',
    textDecoration: 'none',
    borderRadius: '6px',
    transition: 'background-color 0.3s'
  }
};

export default AboutUs;